import {
  Autocomplete,
  Box,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Zoom,
  MenuItem,
} from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { ImgBox, Typography700 } from 'views/common/components';
import StyledBox from 'views/rebranded-module/membership-module/MembershipLandingViewV2/components/AvailableBikesSection/AvailableBikesSection.styles';
import { GrLocation } from 'react-icons/gr';
import axios, { API } from 'lib/config/axios';
import { setLoading } from 'redux/features/ui-slice';
import { useDispatch } from 'react-redux';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { RentalsCard } from 'views/rebranded-module/common/components';
import moneyFormat from 'views/rebranded-module/common/utils/moneyFormat';
import Dinero from 'views/rebranded-module/common/utils/Dinero';
import PATHS from 'lib/navigation/paths';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import EagleRide from 'views/rebranded-module/common/icons/EagleRide';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { useContent } from 'views/rebranded-module/common/context/useContent';

const ALL_CREDITS = 0;

const AvailableBikesSection: FC<any> = (props) => {
  const { availableBikes, particpatingLocations, selectedLocation } = props;

  const content = useContent();
  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();

  const [selectedCredits, setSelectedCredits] = useState<number>(ALL_CREDITS);
  const [componentData, setComponentData] = useState({
    selectedLocation: selectedLocation,
    availableVehicleModels: availableBikes,
  });

  const creditsOptions = useMemo(() => {
    const creditNums = componentData.availableVehicleModels
      ?.map((bike) => bike.credits_num)
      ?.filter((val) => !!val);

    const uniqueCredits = Array.from(new Set(creditNums)).sort((a:any, b:any) => a - b);
    return [ALL_CREDITS, ...uniqueCredits];
  }, [componentData.availableVehicleModels]);

  const availableBikesFiltered = useMemo(() => {
    if (selectedCredits === ALL_CREDITS) {
      return componentData.availableVehicleModels;
    }
    return componentData.availableVehicleModels.filter(
      (bike) => bike.credits_num === selectedCredits
    );
  }, [selectedCredits, componentData.availableVehicleModels]);

  const handleCreditFilterClick = (value: number) => {
    setSelectedCredits(value);
  };

  const getLocationVehicleModelsAndCalendars = (params) => {
    dispatch(setLoading(true));
    axios
      .get(API.membership.LocationVehicleModelsAndCalendars, { params })
      .then((res) => {
        const newModels = res.data.available_vehicle_models;
        const availableCredits = newModels
          .map((bike) => bike.credits_num)
          .filter((val) => !!val);
        const uniqueCredits = Array.from(new Set(availableCredits));
        const isCurrentCreditValid =
          selectedCredits === ALL_CREDITS ||
          uniqueCredits.includes(selectedCredits);

        setComponentData((componentData) => ({
          ...componentData,
          selectedLocation: res.data.selected_location,
          availableVehicleModels: newModels,
        }));

        if (!isCurrentCreditValid) {
          setSelectedCredits(ALL_CREDITS);
        }
      })
      .catch((error) => {
        handleErrorResponse(error);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handleLocationChange = (e, newLoc) => {
    if (!newLoc) return;
    const params = {
      location_id: newLoc?.id,
    };
    getLocationVehicleModelsAndCalendars(params);
  };

  return (
    <StyledBox>
      <Box  display="flex" flexDirection={{ md: 'row', sm: 'column' }} justifyContent="space-between" alignItems={{ md: 'center', sm: 'flex-start' }} gap={3}>
      <Typography700 variant="h4">
        {content.MembershipLandingPage.rentalsSectionTitle}
      </Typography700>

      <Box className="ABSFilterContainer" >
        <Box className="ABSInputContainer">
          <TextField
            select
            variant="outlined"
            className="ABSLocationAC ABSCreditsTextField"
            value={selectedCredits}
            onChange={(e) => handleCreditFilterClick(Number(e.target.value))}
            SelectProps={{
              IconComponent: MdKeyboardArrowDown as React.ElementType,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EagleRide />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value={ALL_CREDITS}>
              {content.MembershipLandingPage.allCreditsFilter}
            </MenuItem>

            {creditsOptions.map((credit) =>
              credit === 0 ? null : (
                <MenuItem key={credit} value={credit}>
                  {credit === 1
                    ? interpolateString(content.MembershipLandingPage.creditsFilterSingular, {
                        number: credit,
                      })
                    : interpolateString(content.MembershipLandingPage.creditsFilterPlural, {
                        number: credit,
                      })}
                </MenuItem>
              )
            )}
          </TextField>
        </Box>

        <Box className="ABSInputContainer">
        <Autocomplete
          disablePortal
          className="ABSLocationAC"
          disableClearable
          options={particpatingLocations || []}
          getOptionLabel={(option: any) => {
              return option.name;
            }}
            popupIcon={MdKeyboardArrowDown({
              className: 'ABSACArrowIcon',
            })}
            value={particpatingLocations.find(
              (loc) => loc.id === componentData?.selectedLocation?.id,
            )}          onChange={handleLocationChange}
          renderInput={(params) => (
            <TextField
              variant="outlined"
              className="ABSLocationAC"
              {...params}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    {GrLocation({})}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />


        </Box>
      </Box>
        </Box>
      {content.MembershipLandingPage.rentalsSectionSubtitle && (
        <Box my={3}>
          <Typography variant="body1">
            {content.MembershipLandingPage.rentalsSectionSubtitle}
          </Typography>
        </Box>
      )}

      <Box mt={4} sx={{ overflow: 'hidden' }}>
        {availableBikesFiltered?.length > 0 ? (
          <Grid container columnSpacing={2} rowSpacing={4}>
            {availableBikesFiltered.map((rental, index) => (
              <Zoom
                key={`${rental.id}-${selectedCredits}`}
                in
                unmountOnExit
                style={{ transitionDelay: `${(index + 1) * 50}ms` }}
              >
                <Grid item lg={4} md={6} sm={12}>
                  <RentalsCard
                    id={rental.id}
                    image={rental.image.medium_url}
                    imageAlt={`${rental.name} image`}
                    title={rental.display_name}
                    name={rental.name_without_make}
                    make={rental.vehicle_make.name}
                    content={content.RentalsCardComponent}
                    startPrice={moneyFormat(Dinero(rental.daily_rate))}
                    specsPath={PATHS.vehicleModel.index(
                      rental.location_slug,
                      rental.make_slug,
                      rental.vehicle_model_slug
                    )}
                    creditsCount={rental.credits_num}
                  />
                </Grid>
              </Zoom>
            ))}
          </Grid>
        ) : (
          <Box className="ABSNoBikesContainer" component={Zoom} in>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
            >
              <ImgBox
                image={{
                  src: '/imgs/membership/no-bike-available.png',
                  alt: 'No available bikes',
                }}
                imgWidth={`112px !important`}
                imgHeight={112}
              />
              <Typography700 variant="h7" component="p" pt={3} pb={2}>
                {content.MembershipLandingPage.noAvailableBikesHeader}
              </Typography700>
              <Typography variant="body3" color="text.secondary">
                {content.MembershipLandingPage.noAvailableBikesSubheader}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </StyledBox>
  );
};

export { AvailableBikesSection };